import type {
  StageDto,
  CreateSavedViewDto,
  FilterOptions,
  FreshnessDto,
  PagedResult,
  QaRawDataResponse,
  RequestDetailDto,
  RequestListItemDto,
  SavedViewDto,
  SavedViewScreen,
  TaskAggregateGroupBy,
  TaskAggregateRowDto,
  TaskInstanceDto,
} from "@gw/shared-types";
import { apiFetch } from "./client";

export function getHealth(): Promise<{ status: string; service: string; version: string }> {
  return apiFetch("/health");
}

/** Server-driven client config (feature flags). Served anonymously from the API. */
export interface ClientConfigDto {
  features: { throughput: boolean; operations: boolean; operationsAssistant: boolean };
}

export function getClientConfig(): Promise<ClientConfigDto> {
  return apiFetch("/config");
}

export function getFreshness(): Promise<FreshnessDto> {
  return apiFetch("/health/freshness");
}

// Param surface mirrors the Slice 3 filter shape so the wire is built early; the API
// silently ignores filter/sort params until Slice 3 wires them server-side.
export interface ListRequestsParams {
  page?: number;
  pageSize?: 25 | 50 | 100 | 200;
  from?: string;
  to?: string;
  sort?: string;
  dir?: "asc" | "desc";
  unit?: "bd" | "cd";
  /** "Onboarding" bucket (RequestType=Onboarding, ≥7 tasks); opt-in. Only emit when true. */
  onboardingsOnly?: boolean;
  /** "Redeployment" bucket (RequestType=Onboarding ≤6 tasks, OR raw RequestType=Redeployment); opt-in. Only emit when true. */
  redeployment?: boolean;
  requestType?: string[];
  status?: string[];
  /** SP-derived two-bucket projection ("Completed" / "InProgress"). */
  calculatedStatus?: string[];
  client?: string[];
  lob?: string[];
  eng?: string[];
  assigned?: string[];
  country?: string[];
  bucket?: string[];
  rework?: boolean;
  /** Live search text (search-as-you-type). */
  q?: string;
  /** Committed search pills — each AND'd with q and with each other. */
  qPills?: string[];
}

function appendMulti(search: URLSearchParams, key: string, values?: string[]): void {
  if (!values) return;
  for (const v of values) search.append(key, v);
}

export function listRequests(
  params: ListRequestsParams = {},
): Promise<PagedResult<RequestListItemDto>> {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.unit) search.set("unit", params.unit);
  if (params.onboardingsOnly) search.set("onboardingsOnly", "true");
  if (params.redeployment) search.set("redeployment", "true");
  if (params.rework) search.set("rework", "true");
  if (params.q) search.set("q", params.q);
  appendMulti(search, "qPills", params.qPills);
  appendMulti(search, "requestType", params.requestType);
  appendMulti(search, "status", params.status);
  appendMulti(search, "calculatedStatus", params.calculatedStatus);
  appendMulti(search, "client", params.client);
  appendMulti(search, "lob", params.lob);
  appendMulti(search, "eng", params.eng);
  appendMulti(search, "assigned", params.assigned);
  appendMulti(search, "country", params.country);
  appendMulti(search, "bucket", params.bucket);
  const qs = search.toString();
  return apiFetch(`/requests${qs ? "?" + qs : ""}`);
}

export function getRequest(
  id: string,
  range?: { from?: string; to?: string },
): Promise<RequestDetailDto> {
  const search = new URLSearchParams();
  if (range?.from) search.set("from", range.from);
  if (range?.to) search.set("to", range.to);
  const qs = search.toString();
  return apiFetch(`/requests/${encodeURIComponent(id)}${qs ? "?" + qs : ""}`);
}

// ---------- Throughput (onboardings started/completed by period) ----------

export type ThroughputGroupBy = "client" | "lob" | "engagementType" | "country" | "assignedTo";

export interface ThroughputBucketDto {
  /** "YYYY-MM" for a month row, or "Total" for the period roll-up. */
  period: string;
  startedCount: number;
  startedWorkers: number;
  completedCount: number;
  completedWorkers: number;
  /** Open at the end of the period: created on/before period end, not yet completed by then. */
  openAtEnd: number;
}

export interface ThroughputGroupRowDto {
  groupValue: string;
  startedCount: number;
  startedWorkers: number;
  completedCount: number;
  completedWorkers: number;
  openAtEnd: number;
}

export interface ThroughputResponseDto {
  months: ThroughputBucketDto[];
  totals: ThroughputBucketDto;
  byGroup: ThroughputGroupRowDto[] | null;
  /** Rail filter options, bundled so the screen needs only this one call. */
  filterOptions: FilterOptions;
}

/** Shared categorical filters for the throughput endpoints (mirror the request-list filters). */
export interface ThroughputFilters {
  from?: string;
  to?: string;
  /** Default true on the server: the full-onboarding (≥7 tasks) bucket. */
  onboardingsOnly?: boolean;
  redeployment?: boolean;
  requestType?: string[];
  client?: string[];
  lob?: string[];
  eng?: string[];
  assigned?: string[];
  country?: string[];
}

export interface ThroughputParams extends ThroughputFilters {
  groupBy?: ThroughputGroupBy;
}

function appendThroughputFilters(search: URLSearchParams, p: ThroughputFilters): void {
  if (p.from) search.set("from", p.from);
  if (p.to) search.set("to", p.to);
  if (p.onboardingsOnly === false) search.set("onboardingsOnly", "false");
  if (p.redeployment) search.set("redeployment", "true");
  appendMulti(search, "requestType", p.requestType);
  appendMulti(search, "client", p.client);
  appendMulti(search, "lob", p.lob);
  appendMulti(search, "eng", p.eng);
  appendMulti(search, "assigned", p.assigned);
  appendMulti(search, "country", p.country);
}

export function getThroughput(params: ThroughputParams = {}): Promise<ThroughputResponseDto> {
  const search = new URLSearchParams();
  appendThroughputFilters(search, params);
  if (params.groupBy) search.set("groupBy", params.groupBy);
  const qs = search.toString();
  return apiFetch(`/requests/throughput${qs ? "?" + qs : ""}`);
}

export type ThroughputBasis = "started" | "completed" | "open";

export interface ThroughputInstancesParams extends ThroughputFilters {
  basis: ThroughputBasis;
  month?: string;
  groupBy?: ThroughputGroupBy;
  groupValue?: string;
}

export interface ThroughputInstancesDto {
  items: RequestListItemDto[];
  totalCount: number;
}

export function getThroughputInstances(
  params: ThroughputInstancesParams,
): Promise<ThroughputInstancesDto> {
  const search = new URLSearchParams();
  appendThroughputFilters(search, params);
  search.set("basis", params.basis);
  if (params.month) search.set("month", params.month);
  if (params.groupBy) search.set("groupBy", params.groupBy);
  if (params.groupValue) search.set("groupValue", params.groupValue);
  return apiFetch(`/requests/throughput/instances?${search.toString()}`);
}

// ---------- QA (hidden raw-Cosmos screen) ----------

export function getQaRawData(id: string): Promise<QaRawDataResponse> {
  return apiFetch(`/qa/requests/${encodeURIComponent(id)}`);
}

export type RequestAggregateGroupBy =
  | "client"
  | "lob"
  | "engagementType"
  | "requestType"
  | "country"
  | "assignedTo"
  | "calculatedStatus";

export interface RequestAggregateRowDto {
  groupValues: Record<string, string>;
  sampleSize: number;
  completed: number;
  inProgress: number;
  medianBd: number | null;
  meanBd: number | null;
  p75Bd: number | null;
  p95Bd: number | null;
  medianCd: number | null;
  meanCd: number | null;
  p75Cd: number | null;
  p95Cd: number | null;
  medianTaskIdleBd: number | null;
  medianTaskIdleCd: number | null;
  medianTaskWaitBd: number | null;
  medianTaskWaitCd: number | null;
  medianTaskExecBd: number | null;
  medianTaskExecCd: number | null;
  medianTaskDurationBd: number | null;
  medianTaskDurationCd: number | null;
  medianPreOnboardingBd: number | null;
  medianPreOnboardingCd: number | null;
  reworkRate: number;
  slowRate: number;
  slowRateCd: number;
  distribution: number[];
  distributionCd: number[];
}

export interface AggregateRequestsParams {
  groupBy: RequestAggregateGroupBy[];
  from?: string;
  to?: string;
  minSample?: number;
  unit?: "bd" | "cd";
  onboardingsOnly?: boolean;
  redeployment?: boolean;
  requestType?: string[];
  status?: string[];
  calculatedStatus?: string[];
  client?: string[];
  lob?: string[];
  eng?: string[];
  assigned?: string[];
  country?: string[];
  bucket?: string[];
  rework?: boolean;
  q?: string;
  qPills?: string[];
}

export function aggregateRequests(
  params: AggregateRequestsParams,
): Promise<RequestAggregateRowDto[]> {
  const search = new URLSearchParams();
  for (const g of params.groupBy) search.append("groupBy", g);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.minSample != null) search.set("minSample", String(params.minSample));
  if (params.unit) search.set("unit", params.unit);
  if (params.onboardingsOnly) search.set("onboardingsOnly", "true");
  if (params.redeployment) search.set("redeployment", "true");
  if (params.rework) search.set("rework", "true");
  if (params.q) search.set("q", params.q);
  appendMulti(search, "qPills", params.qPills);
  appendMulti(search, "requestType", params.requestType);
  appendMulti(search, "status", params.status);
  appendMulti(search, "calculatedStatus", params.calculatedStatus);
  appendMulti(search, "client", params.client);
  appendMulti(search, "lob", params.lob);
  appendMulti(search, "eng", params.eng);
  appendMulti(search, "assigned", params.assigned);
  appendMulti(search, "country", params.country);
  appendMulti(search, "bucket", params.bucket);
  return apiFetch(`/requests/aggregate?${search.toString()}`);
}

export interface AggregateTasksParams {
  groupBy: TaskAggregateGroupBy[];
  from?: string;
  to?: string;
  minSample?: number;
  unit?: "bd" | "cd";
  onboardingsOnly?: boolean;
  redeployment?: boolean;
  requestType?: string[];
  status?: string[];
  client?: string[];
  lob?: string[];
  eng?: string[];
  assigned?: string[];
  country?: string[];
  bucket?: string[];
  rework?: boolean;
  taskCategory?: string[];
  taskName?: string[];
  actorRole?: string[];
  /** Derived task status values: Completed / InProgress / NotStarted / Discarded. */
  taskStatus?: string[];
  q?: string;
  qPills?: string[];
}

export interface ListTaskInstancesParams {
  page?: number;
  pageSize?: 25 | 50 | 100 | 200;
  from?: string;
  to?: string;
  sort?: string;
  dir?: "asc" | "desc";
  unit?: "bd" | "cd";
  onboardingsOnly?: boolean;
  redeployment?: boolean;
  requestType?: string[];
  status?: string[];
  client?: string[];
  lob?: string[];
  eng?: string[];
  assigned?: string[];
  country?: string[];
  bucket?: string[];
  rework?: boolean;
  taskCategory?: string[];
  taskName?: string[];
  actorRole?: string[];
  taskStatus?: string[];
  q?: string;
  qPills?: string[];
}

export function listTaskInstances(
  params: ListTaskInstancesParams,
): Promise<PagedResult<TaskInstanceDto>> {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.unit) search.set("unit", params.unit);
  if (params.rework) search.set("rework", "true");
  if (params.onboardingsOnly) search.set("onboardingsOnly", "true");
  if (params.redeployment) search.set("redeployment", "true");
  appendMulti(search, "requestType", params.requestType);
  appendMulti(search, "status", params.status);
  appendMulti(search, "client", params.client);
  appendMulti(search, "lob", params.lob);
  appendMulti(search, "eng", params.eng);
  appendMulti(search, "assigned", params.assigned);
  appendMulti(search, "country", params.country);
  appendMulti(search, "bucket", params.bucket);
  appendMulti(search, "taskCategory", params.taskCategory);
  appendMulti(search, "taskName", params.taskName);
  appendMulti(search, "actorRole", params.actorRole);
  appendMulti(search, "taskStatus", params.taskStatus);
  if (params.q) search.set("q", params.q);
  appendMulti(search, "qPills", params.qPills);
  return apiFetch(`/tasks/instances?${search.toString()}`);
}

// ---------- Saved views ----------

export function listSavedViews(screen: SavedViewScreen): Promise<SavedViewDto[]> {
  return apiFetch(`/saved-views?screen=${encodeURIComponent(screen)}`);
}

export function createSavedView(dto: CreateSavedViewDto): Promise<SavedViewDto> {
  return apiFetch("/saved-views", {
    method: "POST",
    body: JSON.stringify(dto),
  });
}

export function deleteSavedView(id: string): Promise<void> {
  return apiFetch(`/saved-views/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function aggregateTasks(
  params: AggregateTasksParams,
): Promise<TaskAggregateRowDto[]> {
  const search = new URLSearchParams();
  for (const g of params.groupBy) search.append("groupBy", g);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.minSample != null) search.set("minSample", String(params.minSample));
  if (params.unit) search.set("unit", params.unit);
  if (params.rework) search.set("rework", "true");
  if (params.onboardingsOnly) search.set("onboardingsOnly", "true");
  if (params.redeployment) search.set("redeployment", "true");
  appendMulti(search, "requestType", params.requestType);
  appendMulti(search, "status", params.status);
  appendMulti(search, "client", params.client);
  appendMulti(search, "lob", params.lob);
  appendMulti(search, "eng", params.eng);
  appendMulti(search, "assigned", params.assigned);
  appendMulti(search, "country", params.country);
  appendMulti(search, "bucket", params.bucket);
  appendMulti(search, "taskCategory", params.taskCategory);
  appendMulti(search, "taskName", params.taskName);
  appendMulti(search, "actorRole", params.actorRole);
  appendMulti(search, "taskStatus", params.taskStatus);
  if (params.q) search.set("q", params.q);
  appendMulti(search, "qPills", params.qPills);
  return apiFetch(`/tasks/aggregate?${search.toString()}`);
}

// ---------- Operations dashboard (Class 1 work-in-process control; behind Features:Operations) ----------

export type OperationsPeriodKind = "today" | "weekToDate" | "custom";
export type OperationsStageKey =
  | "requestProcessing"
  | "releaseToSubmit"
  | "submitToApprove"
  | "awaitingClose";
/**
 * Tile keys (Rafael, 2026-10-21): one exclusive state per open item from two clocks. Stuck = untouched for more than
 * stuckAfterBd; Blocked = state unchanged for more than blockedAfterBd; stuckAndBlocked when both hold.
 */
export type OperationsAttentionKey = "clear" | "stuck" | "blocked" | "stuckAndBlocked" | "unknown";
/** Where an open record stands against its work-order version's start date (2026-10-24). */
export type OperationsStartRiskKey = "none" | "onTime" | "atRisk" | "breached";

/**
 * One start-date tile: open journeys (onboarding at any carrier, or an extension) whose work-order version is
 * unapproved and starts within atRiskAfterBd business days (atRisk) or started already (breached). Counted once
 * per journey on the full open population; lensedCount is what the owner columns add up to under a lens.
 */
export interface OperationsStartRiskRowDto {
  risk: "atRisk" | "breached";
  count: number;
  lensedCount: number;
  pctOfOpen: number;
  previousCount: number;
  delta: number;
  onboardings: number;
  extensions: number;
  /** The soonest start (At risk, positive) or the most days late (Breached, negative); null when empty. */
  worstLeadBd: number | null;
}

export interface OperationsStampDto {
  loadedAtUtc: string;
  asOfUtc: string;
  previousBusinessDayCutoffUtc: string;
  calendar: string;
  period: { kind: OperationsPeriodKind; start: string; end: string };
  /** Untouched for more than this many whole business days = Stuck. */
  stuckAfterBd: number;
  /** State (status / actor) unchanged for more than this many whole business days = Blocked. */
  blockedAfterBd: number;
  coverage: { fromUtc: string | null; days: number | null; earliestCreatedInSetUtc: string | null; truncated: boolean };
  activity: { available: boolean; source: "warm" | "lazy" | "none"; builtAtUtc: string | null };
  /** The lens the open-population panels (or the drilled bucket) were cut with; null otherwise. */
  lens: OperationsLensKey | null;
  /** "internal" | "external" when cut by who holds the pen (2026-10-28); null otherwise. */
  pen: OperationsPenKey | null;
  /** Unapproved and starting within this many business days = At risk (Operations:AtRiskBusinessDays). */
  atRiskAfterBd: number;
  /** Unapproved this many business days past the start date = Breached (Operations:BreachedAfterBusinessDays; 0 = on the start date). */
  breachedAfterBd: number;
}

/** Attention lens values. "unknown" is a data condition, not something to look through. */
export type OperationsLensKey = "stuck" | "blocked" | "stuckAndBlocked" | "clear";
/** Who holds the pen (2026-10-28): a second lens beside Attention, and the value every open item carries. */
export type OperationsPenKey = "internal" | "external";

export interface OperationsFlowRowDto {
  workType: string;
  /** Lensed when a lens is on. */
  openNow: number;
  /** The full open population (equal to openNow without a lens). */
  openNowAll: number;
  started: number;
  ended: number;
  net: number;
}

export interface OperationsAttentionSplitDto {
  /** clear + stuck + blocked + stuckAndBlocked + unknown. */
  total: number;
  clear: number;
  /** stuck + blocked + stuckAndBlocked. */
  stuckOrBlocked: number;
  unknown: number;
  /** Stuck only. */
  stuck: number;
  /** Blocked only. */
  blocked: number;
  /** Both clocks over. */
  stuckAndBlocked: number;
}

export interface OperationsStatusRowDto {
  status: string;
  split: OperationsAttentionSplitDto;
}

export interface OperationsStageRowDto {
  stage: OperationsStageKey;
  split: OperationsAttentionSplitDto;
  previousTotal: number;
  delta: number;
  fidelity: "approximate";
  byStatus: OperationsStatusRowDto[];
  /** Median business days the stage's waiting items have sat on their first open task or work order (Pending for); null when none waits (2026-10-29). */
  pendingMedianBd?: number | null;
}

/** Which record's clocks flagged an item: the task in progress, or the unapproved work order waiting with no task pending. */
export type OperationsStuckBy = "pendingTask" | "workOrder";

export interface OperationsAttentionRowDto {
  state: OperationsAttentionKey;
  count: number;
  pctOfOpen: number;
  previousCount: number;
  delta: number;
  /** Flagged items whose clocks come from the waiting work order rather than a task; 0 on Clear / Unknown. */
  fromWorkOrder: number;
}

export interface OperationsAgeBinDto {
  bin: number;
  label: string;
  total: number;
  byWorkType: Record<string, number>;
  /** stuck + blocked (exclusive states, so the sum). */
  stuckOrBlocked: number;
  stuck: number;
  blocked: number;
}

export interface OperationsArrivalDayDto {
  day: string;
  arrivals: number;
  departures: number;
  openAtEnd: number;
}

export interface OperationsOwnerRowDto {
  owner: string;
  /** The manager's name in the provisioning directory; "No team" when unmatched (2026-10-23). */
  team: string | null;
  department: string | null;
  /** The directory's line of business, shown as Division (the request's line of business is another thing). */
  division: string | null;
  /** The manager of the owner's team (the team lead's manager, 2026-10-27); "No manager" when unmatched. */
  manager: string | null;
  openOwned: number;
  stuck: number;
  blocked: number;
  oldestItemBd: number | null;
  /** The longest any of the owner's open items has been waiting (pending task or work order), in whole business days. */
  maxPendingBd: number | null;
  opening: number;
  newAssigned: number;
  departures: number;
  closing: number;
  identityHolds: boolean;
  /** Open journeys carried by this owner's records, each once at its work type's weight (2026-10-23). */
  weightedOpen: number;
  /** How many journeys the weighted figure counts. */
  weightedJourneys: number;
  /** The weighted figure by work type, in Flow row order. */
  workload: OperationsWorkloadPartDto[];
  /** Open journeys carried by this owner's records that start within atRiskAfterBd bd, unapproved (2026-10-24). */
  atRisk: number;
  /** ... that started already, unapproved. */
  breached: number;
}

export interface OperationsWorkloadPartDto {
  workType: string;
  count: number;
  weight: number;
  weighted: number;
}

export interface OperationsCheckDto {
  name: string;
  pass: boolean;
  expected: number;
  actual: number;
  detail: string | null;
}

export interface OperationsSummaryDto {
  stamp: OperationsStampDto;
  /** Open items in the lens (= attention.openNow without one): what flow open-now, stages, age and owners add up to. */
  lensedOpenNow: number;
  /**
   * The Onboarding and Redeployment rows count consultant journeys (intake + pre-verification + request = one);
   * the other rows count requests. `note` is set when the journeys could not include intakes (read failed).
   */
  flow: { rows: OperationsFlowRowDto[]; total: OperationsFlowRowDto; note?: string | null };
  wipByStage: OperationsStageRowDto[];
  attention: { openNow: number; previousOpen: number; openDelta: number; states: OperationsAttentionRowDto[] };
  age: {
    binsBusinessDays: OperationsAgeBinDto[];
    p50Bd: number;
    p85Bd: number;
    sample: number;
  };
  arrivals: OperationsArrivalDayDto[];
  owners: OperationsOwnerRowDto[];
  ownerTotal: OperationsOwnerRowDto;
  checks: OperationsCheckDto[];
  filterOptions: FilterOptions;
  /** The intake row above the stages; not part of lensedOpenNow. */
  intake: OperationsIntakeRowDto;
  /** The two start-date tiles (2026-10-24): At risk, Breached. */
  startRisk: OperationsStartRiskRowDto[];
  /** The weights behind the owner table's Weighted open column (settings). */
  workTypeWeights: Record<string, number>;
  defaultWorkTypeWeight: number;
  /** Whether to show the consistency checks strip (setting Operations:ShowConsistencyChecks, default off); the checks are always present. */
  showConsistencyChecks: boolean;
  /** The provisioning directory behind Team / Division (2026-10-26): its size and the owners it cannot place. */
  directory: OperationsDirectoryDto;
}

export interface OperationsDirectoryDto {
  /** 0 = the directory was never read: every owner shows under No team. */
  users: number;
  /** Owners that no alias, e-mail or unique full name finds in the directory, with the e-mail their records carry. */
  unmatchedOwners: OperationsUnmatchedOwnerDto[];
}

export interface OperationsUnmatchedOwnerDto {
  owner: string;
  email: string | null;
}

export interface OperationsItemDto {
  request: RequestListItemDto;
  /** The Operations owner: the work order's assignee, else the request's, else "Unassigned". */
  owner: string;
  workType: string;
  stage: OperationsStageKey | null;
  /** The one tile the item is in; empty for closed items. */
  attention: OperationsAttentionKey[];
  status: string;
  ageBd: number;
  /** Whole business days the item has been waiting on `waitingOn`; null when nothing is pending. */
  pendingForBd: number | null;
  stuckBy: OperationsStuckBy | null;
  waitingOn: OperationsWaitingOnDto | null;
  /** Who holds the pen (2026-10-28): "internal" | "external"; null for closed items. */
  holder: OperationsPenKey | null;
  /** The touch clock: the last event on the record and whole business days since; null when unknown / closed. */
  lastTouchedAtUtc: string | null;
  untouchedBd: number | null;
  /** The state clock: when the record last moved state and whole business days since; null when nothing carries a state. */
  stateSinceUtc: string | null;
  sinceStateBd: number | null;
  /** Start-date risk (2026-10-24): the flag, the signed lead in business days (negative = already started) and the version's start date. */
  startRisk: OperationsStartRiskKey;
  startLeadBd: number | null;
  startDate: string | null;
}

/**
 * What an open item is waiting on: the first open task action in FBO order (who holds it) or, with
 * no task pending, the unapproved work order counted from the last progress.
 */
export interface OperationsWaitingOnDto {
  kind: "task" | "workOrder";
  /** ActorRole name for a task ("Consultant", "ClientService", "Other", ...); null for a work order. */
  role: string | null;
  /** The sproc's own role string, shown when role is "Other". */
  roleRaw: string | null;
  /** The actor's name for a task; "to submit" / "to approve" for a work order. */
  detail: string | null;
  sinceUtc: string;
  businessDays: number;
  /** The FBO task behind a "task" entry; null for a work order. */
  taskId: string | null;
  taskName: string | null;
}

/** One open task action as of the snapshot; `queued` = behind the deciding action (tasks run in sequence). */
export interface OperationsPendingActionDto {
  taskId: string | null;
  taskName: string | null;
  role: string;
  roleRaw: string | null;
  actorName: string | null;
  assignedAtUtc: string;
  businessDays: number;
  queued: boolean;
}

/** Why one request is Stuck / Blocked / Clear right now (GET /operations/items/{id}/explain). */
export interface OperationsItemExplanationDto {
  item: OperationsItemDto;
  /** The same item replayed at the previous-business-day cutoff (approximate). */
  previous: OperationsItemDto;
  pending: OperationsPendingActionDto[];
  stuckAfterBd: number;
  blockedAfterBd: number;
  workOrderSubmittedAtUtc: string | null;
  workOrderApprovedAtUtc: string | null;
  /** When the sprocs carry them (2026-10-21): the work order's last edit and its latest workflow action. */
  workOrderLastModifiedAtUtc: string | null;
  workOrderLastActionAtUtc: string | null;
  requestCompletedAtUtc: string | null;
  stamp: OperationsStampDto;
}

export interface OperationsFacetCountDto {
  value: string;
  count: number;
}

/**
 * Drawer facet counts over the whole bucket (not capped). Each facet is counted with the other
 * facet's selection and the search applied, but not its own.
 */
export interface OperationsItemsFacetsDto {
  attention: OperationsFacetCountDto[];
  /** An ActorRole name ("Consultant", "SubvendorWorker", "ClientService", ...), "WorkOrder" or "None". */
  pendingWith: OperationsFacetCountDto[];
  /** Flow row labels (Onboarding, Extension, ...), in Flow row order. */
  workType: OperationsFacetCountDto[];
  /** breached, atRisk, onTime, none (2026-10-24). */
  startRisk: OperationsFacetCountDto[];
  /** Operations owner keys by count, Unassigned last (2026-10-26). */
  owner: OperationsFacetCountDto[];
  /** The owners' teams by count, No team last. */
  team: OperationsFacetCountDto[];
  /** The owners' divisions by count, No division last (2026-10-26). */
  division: OperationsFacetCountDto[];
  /** The managers of the owners' teams by count, No manager last (2026-10-27). */
  manager: OperationsFacetCountDto[];
  /** The requests' engagement types by count, Not set last (2026-10-28). */
  engagement: OperationsFacetCountDto[];
  /** internal / external by count (2026-10-28). */
  holder: OperationsFacetCountDto[];
}

export type OperationsIntakeSourceKey = "ats" | "client" | "manual" | "vms";

/**
 * The "Request (Intake)" row of the stage card: intakes (ATS placement, Client, Manual, VMS) that do
 * not exist in FBO yet. A separate population: never part of lensedOpenNow, the tiles, the owners or
 * the checks. Unavailable (with a reason) when the intake sync is off or its table could not be read.
 */
export interface OperationsIntakeRowDto {
  available: boolean;
  reason: string | null;
  /** Open intakes in the lens, by attention (unknown is always 0). */
  split: OperationsAttentionSplitDto;
  /** Open intakes over the full population, for the "N of M" legend under a lens. */
  openNowAll: number;
  previousTotal: number;
  delta: number;
  fidelity: "approximate";
  /** Untouched for more than this many business days = Stuck. */
  stuckAfterBd: number;
  /** Status unchanged for this many business days = Blocked (wins over Stuck). */
  blockedAfterBd: number;
  /** Intakes untouched for more than this many business days are on no card (0 = none hidden). */
  hideAfterUntouchedBd: number;
}

export interface OperationsIntakeItemDto {
  source: OperationsIntakeSourceKey;
  id: string;
  worker: string | null;
  client: string | null;
  /** The assigned user; null = unassigned. */
  pendingWith: string | null;
  status: string | null;
  attention: OperationsLensKey;
  /** Who holds the pen: always \"internal\" (an intake is Procom's), 2026-10-28. */
  holder: OperationsPenKey;
  /** Dashboard work type (2026-10-22): Onboarding, Extension, ChangeRequest, Redeployment, Termination, Other. Only Onboarding intakes are journeys. */
  workType: string;
  /** The two states in which an intake is open work: nothing in FBO yet, or its work order waits for a request. */
  journeyState: "notInFbo" | "awaitingRequest";
  /** "status New for 9 bd" / "untouched for 2 bd" / "WO created 4 bd ago, no request yet"; null when clear. */
  reason: string | null;
  waitingBd: number;
  untouchedBd: number;
  sinceStatusBd: number;
  /** ATS: the ATS name (ProcomBullhorn); VMS: the VMS type (Beeline). */
  sourceSystem: string | null;
  /** ATS: the placement id; VMS: the external request id. */
  sourceReference: string | null;
  preOnboardingRequestId: string | null;
  workOrderId: number | null;
  startedUtc: string;
  /** While awaiting its request: the work-order version's start date against today (2026-10-24). */
  startRisk: OperationsStartRiskKey;
  startLeadBd: number | null;
  startDate: string | null;
}

export interface OperationsIntakeItemsDto {
  items: OperationsIntakeItemDto[];
  totalCount: number;
  cap: number;
  /** Counts over the lensed open population, before the attention / source / search filters. */
  attentionFacets: OperationsFacetCountDto[];
  sourceFacets: OperationsFacetCountDto[];
  stateFacets: OperationsFacetCountDto[];
  /** Open intakes per work type, Onboarding first. */
  workTypeFacets: OperationsFacetCountDto[];
  asOfUtc: string;
  lens: OperationsLensKey | null;
  /** "internal" | "external" when cut by who holds the pen (2026-10-28); null otherwise. */
  pen: OperationsPenKey | null;
  stuckAfterBd: number;
  blockedAfterBd: number;
}

export interface OperationsIntakeItemsFilter {
  attention?: OperationsLensKey;
  source?: OperationsIntakeSourceKey;
  q?: string;
  /** The owner table's desk views (2026-10-27): the intakes a person / team / division holds while a journey waits at intake. */
  owner?: string;
  ownerTeam?: string;
  ownerDivision?: string;
  ownerManager?: string;
}

export type OperationsJourneyFlowKey = "open" | "started" | "ended";
/** Which record carries the journey now: the intake, the pre-verification, the intake again while the WO waits for its request, the request, or nobody. */
export type OperationsJourneyCarrierKey = "intake" | "preOnboarding" | "awaitingRequest" | "request" | "workOrder" | "none";

export interface OperationsJourneyLinkDto {
  kind: "intake" | "request";
  /** Set for an intake link. */
  source: OperationsIntakeSourceKey | null;
  id: string;
}

export interface OperationsJourneyIntakeDto {
  source: OperationsIntakeSourceKey;
  id: string;
  status: string | null;
  sourceSystem: string | null;
  sourceReference: string | null;
}

/**
 * One consultant journey behind the Flow card's Onboarding / Redeployment rows: its intake, pre-verification
 * and onboarding request counted once. Started = the earliest record; ended = the work order's approval.
 */
export interface OperationsJourneyItemDto {
  key: string;
  /** Onboarding or Redeployment for a journey; any request type for the other open requests of an age-bin cohort. */
  workType: string;
  worker: string | null;
  client: string | null;
  carrier: OperationsJourneyCarrierKey;
  /** Where to open the carrying record; null when nobody carries it (approved, or WO without a record yet). */
  carrierLink: OperationsJourneyLinkDto | null;
  intake: OperationsJourneyIntakeDto | null;
  preOnboardingRequestId: string | null;
  onboardingRequestId: string | null;
  workOrderId: number | null;
  pendingWith: string | null;
  attention: OperationsLensKey | "unknown";
  reason: string | null;
  /** Whole business days since the journey started. */
  waitingBd: number;
  startedUtc: string;
  endedUtc: string | null;
  /** The carrier's start-date flag (2026-10-24). */
  startRisk: OperationsStartRiskKey;
  startLeadBd: number | null;
  startDate: string | null;
  /** Where the carrying request stands (2026-10-26): its Operations stage; null while an intake carries. */
  stage: OperationsStageKey | null;
  /** Open task actions on the carrying request; null while an intake carries. */
  openTaskCount: number | null;
  /** Who holds it: "task" (pendingWith is the actor, pendingRole the role), "workOrder" (pendingWith is the step), "intake" (the intake's owner), or null. */
  pendingKind: "task" | "workOrder" | "intake" | null;
  pendingRole: string | null;
  /** The owner of the carrying record and that owner's team and division (2026-10-26); null when nobody carries it. */
  owner: string | null;
  team: string | null;
  division: string | null;
  /** The manager of that owner's team (2026-10-27). */
  manager: string | null;
  /** The carrying request's engagement type, else the latest request's, else "Not set" (2026-10-28). */
  engagementType: string;
  /** Who holds the pen: the carrier's side, an intake-carried journey Procom's; null when not open (2026-10-28). */
  holder: OperationsPenKey | null;
}

export interface OperationsJourneyItemsDto {
  items: OperationsJourneyItemDto[];
  totalCount: number;
  cap: number;
  /** Counts over the cohort, before the chips and the search. */
  attentionFacets: OperationsFacetCountDto[];
  carrierFacets: OperationsFacetCountDto[];
  /** Work types in the cohort; more than one label only on an age-bin cohort. */
  workTypeFacets: OperationsFacetCountDto[];
  /** breached, atRisk, onTime, none (2026-10-24). */
  startRiskFacets: OperationsFacetCountDto[];
  /** Ignored by the API when ageBin is set. */
  flow: OperationsJourneyFlowKey;
  /** The age card's bar this cohort is (business days), or null for a Flow cohort. */
  ageBin: number | null;
  asOfUtc: string;
  lens: OperationsLensKey | null;
  /** "internal" | "external" when cut by who holds the pen (2026-10-28); null otherwise. */
  pen: OperationsPenKey | null;
  /** Set when intakes could not be read: the journeys are counted from requests only. */
  note: string | null;
}

export interface OperationsJourneyItemsFilter {
  flow: OperationsJourneyFlowKey;
  /** Any Flow row label (every row is a journey row since 2026-10-22). */
  workType?: string;
  attention?: OperationsLensKey;
  carrier?: OperationsJourneyCarrierKey;
  q?: string;
  /** The age card's bar: journeys and the other open requests of that age; flow is then ignored. */
  ageBin?: number;
  /** The start-date tiles' cohort (2026-10-24). */
  startRisk?: OperationsStartRiskKey;
}

export type IntakeJourneyStateKey = "notInFbo" | "inPreOnboarding" | "awaitingRequest" | "inRequest" | "offboarding" | "complete" | "canceled";

export interface IntakeJourneyRequestDto {
  id: string;
  requestType: string;
  status: string;
  calculatedStatus: string | null;
  createdDateTime: string;
  completedAt: string | null;
  workerName: string;
}

export interface IntakeWorkOrderDto {
  workOrderId: number;
  workOrderVersionId: number | null;
  assignmentId: number | null;
  createdDateTime: string | null;
  submitDateTime: string | null;
  approveDateTime: string | null;
  rejectCount: number;
  submitCount: number;
  approver: string | null;
  startDate: string | null;
  endDate: string | null;
  statusId: number | null;
  versionStatusId: number | null;
}

export interface IntakeJourneyDto {
  state: IntakeJourneyStateKey;
  stateSince: string;
  intakeIsOpen: boolean;
  intakeDoneAt: string | null;
  preOnboardingRequestId: string | null;
  preOnboarding: IntakeJourneyRequestDto | null;
  workOrder: IntakeWorkOrderDto | null;
  /** Requests on the work order, oldest first. */
  requests: IntakeJourneyRequestDto[];
  /** The request snapshot was warming: requests may be missing from the journey. */
  requestsUnavailable: boolean;
}

export interface IntakeAttentionDto {
  state: OperationsLensKey;
  reason: string | null;
  waitingBd: number;
  untouchedBd: number;
  sinceStatusBd: number;
  stuckAfterBd: number;
  blockedAfterBd: number;
}

/** GET /intake/{source}/{id}: one intake request and where its journey stands. */
export interface IntakeDetailDto {
  source: OperationsIntakeSourceKey;
  id: string;
  documentType: string | null;
  status: string | null;
  statusReason: string | null;
  requestType: string | null;
  requestSubType: string | null;
  startedDateTime: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  lastModifiedByUserName: string | null;
  statusChangedDateTime: string;
  completedDateTime: string | null;
  discardedDateTime: string | null;
  discardedReason: string | null;
  endedDateTime: string | null;
  isDeleted: boolean;
  workerName: string | null;
  workerEmail: string | null;
  workerOrganizationName: string | null;
  workerProfileType: string | null;
  clientName: string | null;
  clientFboId: number | null;
  assignedToName: string | null;
  assignedToEmail: string | null;
  jobTitle: string | null;
  startDate: string | null;
  endDate: string | null;
  lineOfBusiness: string | null;
  hiringManagerName: string | null;
  requestedByName: string | null;
  sourceSystem: string | null;
  sourceReference: string | null;
  syncedAtUtc: string | null;
  journey: IntakeJourneyDto;
  /** Null when the intake is not the record carrying the journey. */
  attention: IntakeAttentionDto | null;
  stages: StageDto[];
  asOfUtc: string;
}

export interface OperationsItemsDto {
  items: OperationsItemDto[];
  totalCount: number;
  cap: number;
  stamp: OperationsStampDto;
  facets: OperationsItemsFacetsDto;
  /** Items in the drilled bucket before the drawer's chips and search (totalCount is after them). */
  bucketCount: number;
}

export type OperationsItemsSortKey =
  | "worker" | "client" | "type" | "stage" | "age" | "pendingFor" | "attention" | "pendingWith" | "owner" | "startLead";

/** Categorical filters + period shared by both operations endpoints (mirror the API query names). */
export interface OperationsParams {
  period?: "today" | "wtd" | "custom";
  from?: string;
  to?: string;
  lens?: OperationsLensKey;
  /** Who holds the pen: the second lens (2026-10-28). */
  pen?: OperationsPenKey;
  onboardingsOnly?: boolean;
  redeployment?: boolean;
  requestType?: string[];
  client?: string[];
  lob?: string[];
  eng?: string[];
  assigned?: string[];
  /** The owner's team (the manager's name in the provisioning directory); "No team" for unmatched owners. */
  team?: string[];
  /** The owner's division (the provisioning directory's line of business); "No division" for unmatched owners. */
  division?: string[];
  /** The manager of the owner's team (the team lead's manager, 2026-10-27); "No manager" for unmatched owners. */
  manager?: string[];
  country?: string[];
}

/** Which bucket of the summary to list. All optional and ANDed server-side. */
export interface OperationsItemsFilter {
  attention?: OperationsAttentionKey;
  stage?: OperationsStageKey;
  status?: string;
  owner?: string;
  /** The owner table's team / division row (2026-10-26): the owner's team / division in the provisioning directory. */
  ownerTeam?: string;
  ownerDivision?: string;
  /** The owner table's manager row (2026-10-27). */
  ownerManager?: string;
  ageBin?: number;
  workType?: string;
  flow?: "open" | "started" | "ended";
  day?: string;
  dayKind?: "arrival" | "departure";
  stuckBy?: OperationsStuckBy;
  /** The start-date bucket (2026-10-24). */
  startRisk?: OperationsStartRiskKey;
  // Drawer view, applied inside the bucket on the server (so it covers items past the cap).
  attentionIn?: OperationsAttentionKey[];
  pendingWith?: string[];
  workTypeIn?: string[];
  startRiskIn?: OperationsStartRiskKey[];
  ownerIn?: string[];
  teamIn?: string[];
  divisionIn?: string[];
  managerIn?: string[];
  engagementIn?: string[];
  /** The drawer's "Who holds the pen" chips (2026-10-28). */
  holderIn?: OperationsPenKey[];
  q?: string;
  sort?: OperationsItemsSortKey;
  dir?: "asc" | "desc";
}

function appendOperationsParams(search: URLSearchParams, p: OperationsParams): void {
  if (p.period) search.set("period", p.period);
  if (p.from) search.set("from", p.from);
  if (p.to) search.set("to", p.to);
  if (p.lens) search.set("lens", p.lens);
  if (p.pen) search.set("pen", p.pen);
  if (p.onboardingsOnly) search.set("onboardingsOnly", "true");
  if (p.redeployment) search.set("redeployment", "true");
  appendMulti(search, "requestType", p.requestType);
  appendMulti(search, "client", p.client);
  appendMulti(search, "lob", p.lob);
  appendMulti(search, "eng", p.eng);
  appendMulti(search, "assigned", p.assigned);
  appendMulti(search, "team", p.team);
  appendMulti(search, "division", p.division);
  appendMulti(search, "manager", p.manager);
  appendMulti(search, "country", p.country);
}

export function getOperationsSummary(params: OperationsParams = {}): Promise<OperationsSummaryDto> {
  const search = new URLSearchParams();
  appendOperationsParams(search, params);
  const qs = search.toString();
  return apiFetch(`/operations/summary${qs ? "?" + qs : ""}`);
}

/** The intakes behind the "Request (Intake)" row. The request slicers apply as on the Flow card: client and assigned-to by the intake's own fields, any other empties it. */
export function getOperationsIntakeItems(params: OperationsParams = {}, filter: OperationsIntakeItemsFilter = {}): Promise<OperationsIntakeItemsDto> {
  const search = new URLSearchParams();
  appendOperationsParams(search, params);
  if (filter.attention) search.set("attention", filter.attention);
  if (filter.source) search.set("source", filter.source);
  if (filter.q) search.set("q", filter.q);
  if (filter.owner) search.set("owner", filter.owner);
  if (filter.ownerTeam) search.set("ownerTeam", filter.ownerTeam);
  if (filter.ownerDivision) search.set("ownerDivision", filter.ownerDivision);
  if (filter.ownerManager) search.set("ownerManager", filter.ownerManager);
  const qs = search.toString();
  return apiFetch(`/operations/intake/items${qs ? "?" + qs : ""}`);
}

/** The journeys behind the Flow card's Onboarding / Redeployment rows. The request slicers apply through the journey's requests. */
export function getOperationsJourneyItems(params: OperationsParams, filter: OperationsJourneyItemsFilter): Promise<OperationsJourneyItemsDto> {
  const search = new URLSearchParams();
  appendOperationsParams(search, params);
  if (filter.flow !== "open") search.set("flow", filter.flow);
  if (filter.workType) search.set("workType", filter.workType);
  if (filter.attention) search.set("attention", filter.attention);
  if (filter.carrier) search.set("carrier", filter.carrier);
  if (filter.q?.trim()) search.set("q", filter.q.trim());
  if (filter.ageBin !== undefined) search.set("ageBin", String(filter.ageBin));
  if (filter.startRisk) search.set("startRisk", filter.startRisk);
  const qs = search.toString();
  return apiFetch(`/operations/journeys${qs ? "?" + qs : ""}`);
}

/** 404 when the intake is unknown; 400 for a source other than ats / client / manual / vms. */
// ---------- Journey detail (one consultant journey on one page, 2026-10-21) ----------

export type JourneyStageKey = "intake" | "preOnboarding" | "awaitingRequest" | "preRelease" | "requestProcessing" | "woSubmit" | "woApproval" | "total";
export type JourneyStageStatus = "done" | "current" | "notStarted" | "skipped";
export type JourneyStateKey = "notInFbo" | "inPreOnboarding" | "awaitingRequest" | "inRequest" | "offboarding" | "complete" | "canceled";

export interface JourneyStageDto {
  key: JourneyStageKey;
  status: JourneyStageStatus;
  start: string | null;
  end: string | null;
  bd: number | null;
  cd: number | null;
  /** Peer median of the stage in business days; null for Intake (no peer data) or a small cohort. */
  peerMedianBd: number | null;
  peerCount: number | null;
  /** WO approval only: the work order was sent back at least once. */
  sentBack: boolean;
}

export interface JourneyPeerGroupDto {
  sampleSize: number;
  level: number | null;
  levelDescription: string | null;
}

export interface JourneyIntakeRecordDto {
  source: OperationsIntakeSourceKey;
  id: string;
  sourceSystem: string | null;
  sourceReference: string | null;
  status: string | null;
  startedDateTime: string;
  lastModifiedDateTime: string;
  statusChangedDateTime: string;
  endedDateTime: string | null;
  assignedToName: string | null;
  jobTitle: string | null;
  startDate: string | null;
  journeyState: JourneyStateKey;
  isOpen: boolean;
}

export interface JourneyRequestRecordDto {
  id: string;
  requestType: string;
  status: string;
  calculatedStatus: string | null;
  completionRule: string | null;
  createdDateTime: string;
  releasedDateTime: string | null;
  requestCompletedDateTime: string | null;
  completedAt: string | null;
  assignedToUserName: string | null;
  workOrderId: number | null;
  workOrderNumber: string | null;
}

export interface JourneyWorkOrderDto {
  workOrderId: number;
  workOrderNumber: string | null;
  createdDateTime: string | null;
  submitDateTime: string | null;
  approveDateTime: string | null;
  approver: string | null;
  submitCount: number;
  rejectCount: number;
  lastActionDateTime: string | null;
  lastModifiedDateTime: string | null;
  /** When FBO terminated the work order; the end of a termination journey. */
  offboardingCompletionDate: string | null;
  startDate: string | null;
  endDate: string | null;
  /** The record's own version's contract dates (2026-10-24); what the start-date flag reads. */
  versionStartDate: string | null;
  /** The version and assignment behind FBO's work-order page (/next/workorder/{assignment}/{workOrder}/{version}/core/) and the version's assignee (2026-10-27). */
  workOrderVersionId?: number | null;
  assignmentId?: number | null;
  assignedToUserName?: string | null;
  versionEndDate: string | null;
}

export interface JourneyDetailDto {
  /** Onboarding | Redeployment | Extension | ChangeRequest | DocumentRequest | ... */
  kind: string;
  state: JourneyStateKey;
  carrier: OperationsJourneyCarrierKey;
  worker: string | null;
  client: string | null;
  intake: JourneyIntakeRecordDto | null;
  preOnboarding: JourneyRequestRecordDto | null;
  /** The request the page is about, with its tasks: the onboarding (or other primary request), else the pre-verification. */
  request: RequestDetailDto | null;
  /** Set when the carried request is the pre-verification and an onboarding exists on the work order (listed without its tasks). */
  primaryRequestId: string | null;
  workOrder: JourneyWorkOrderDto | null;
  siblings: JourneyRequestRecordDto[];
  stages: JourneyStageDto[];
  peerGroup: JourneyPeerGroupDto;
  requestAttention: OperationsItemExplanationDto | null;
  intakeAttention: IntakeAttentionDto | null;
  stuckAfterBd: number;
  blockedAfterBd: number;
  note: string | null;
  asOfUtc: string;
  /** The carrying record's start-date flag (2026-10-24), its signed lead in business days, the version's start date, the window. */
  startRisk: OperationsStartRiskKey;
  startLeadBd: number | null;
  startDate: string | null;
  atRiskAfterBd: number;
  breachedAfterBd: number;
}

export function getJourneyByRequest(id: string): Promise<JourneyDetailDto> {
  return apiFetch(`/journeys/request/${encodeURIComponent(id)}`);
}

export function getJourneyByIntake(source: string, id: string): Promise<JourneyDetailDto> {
  return apiFetch(`/journeys/intake/${encodeURIComponent(source)}/${encodeURIComponent(id)}`);
}

export function getIntakeDetail(source: string, id: string): Promise<IntakeDetailDto> {
  return apiFetch(`/intake/${encodeURIComponent(source)}/${encodeURIComponent(id)}`);
}

/** 404 when the request is not in the Operations dataset (Draft, Cancelled, outside the coverage window). */
export function getOperationsItemExplanation(requestId: string): Promise<OperationsItemExplanationDto> {
  return apiFetch(`/operations/items/${encodeURIComponent(requestId)}/explain`);
}

export function getOperationsItems(
  params: OperationsParams,
  filter: OperationsItemsFilter,
): Promise<OperationsItemsDto> {
  const search = new URLSearchParams();
  appendOperationsParams(search, params);
  if (filter.attention) search.set("attention", filter.attention);
  if (filter.stage) search.set("stage", filter.stage);
  if (filter.status) search.set("status", filter.status);
  if (filter.owner) search.set("owner", filter.owner);
  if (filter.ownerTeam) search.set("ownerTeam", filter.ownerTeam);
  if (filter.ownerDivision) search.set("ownerDivision", filter.ownerDivision);
  if (filter.ownerManager) search.set("ownerManager", filter.ownerManager);
  if (filter.ageBin != null) search.set("ageBin", String(filter.ageBin));
  if (filter.workType) search.set("workType", filter.workType);
  if (filter.flow) search.set("flow", filter.flow);
  if (filter.day) search.set("day", filter.day);
  if (filter.dayKind) search.set("dayKind", filter.dayKind);
  if (filter.stuckBy) search.set("stuckBy", filter.stuckBy);
  if (filter.startRisk) search.set("startRisk", filter.startRisk);
  appendMulti(search, "attentionIn", filter.attentionIn);
  appendMulti(search, "pendingWith", filter.pendingWith);
  appendMulti(search, "workTypeIn", filter.workTypeIn);
  appendMulti(search, "startRiskIn", filter.startRiskIn);
  appendMulti(search, "ownerIn", filter.ownerIn);
  appendMulti(search, "teamIn", filter.teamIn);
  appendMulti(search, "divisionIn", filter.divisionIn);
  appendMulti(search, "managerIn", filter.managerIn);
  appendMulti(search, "engagementIn", filter.engagementIn);
  appendMulti(search, "holderIn", filter.holderIn);
  if (filter.q?.trim()) search.set("q", filter.q.trim());
  if (filter.sort) search.set("sort", filter.sort);
  if (filter.dir) search.set("dir", filter.dir);
  return apiFetch(`/operations/items?${search.toString()}`);
}
